import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { PropertyEditor } from '../property-editor';
import { PropertyInput } from './input-base';

export class AngleInput extends PropertyInput {
    protected cursorPosition: number = 0;
    protected isSelected: boolean = false;

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: number,
        isActive: boolean,
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor
    ) {
        super(ctx, x, y, width, height, value, isActive, levelDesigner, propertyEditor);
    }

    draw(): void {
        // Draw input background
        this.ctx.fillStyle = 'white';
        this.ctx.strokeStyle = this.isActive ? '#0078D4' : '#CCCCCC';
        this.ctx.lineWidth = 1;
        this.ctx.fillRect(this.x, this.y, this.width, this.height);
        this.ctx.strokeRect(this.x, this.y, this.width, this.height);

        // Draw text
        this.ctx.fillStyle = 'black';
        this.ctx.font = '12px Arial';
        this.ctx.textBaseline = 'middle';
        const textX = this.x + 5;
        const textY = this.y + this.height / 2;

        // Draw text with cursor if active
        if (this.isActive) {
            if (this.isSelected) {
                // Draw selection background
                this.ctx.fillStyle = '#0078D4';
                const textWidth = this.ctx.measureText(this.displayValue).width;
                this.ctx.fillRect(textX, this.y + 2, textWidth, this.height - 4);
                this.ctx.fillStyle = 'white';
            }
            this.ctx.fillText(this.displayValue + '°', textX, textY);

            if (!this.isSelected) {
                // Draw cursor
                const textBeforeCursor = this.displayValue.substring(0, this.cursorPosition);
                const cursorX = textX + this.ctx.measureText(textBeforeCursor).width;
                this.ctx.beginPath();
                this.ctx.moveTo(cursorX, this.y + 4);
                this.ctx.lineTo(cursorX, this.y + this.height - 4);
                this.ctx.strokeStyle = 'black';
                this.ctx.stroke();
            }
        } else {
            this.ctx.fillText(this.displayValue + '°', textX, textY);
        }
    }

    handleClick(x: number, y: number): boolean {
        const isInInputBounds = x >= this.x && x <= this.x + this.width && 
                               y >= this.y && y <= this.y + this.height;

        if (!isInInputBounds) {
            return false;
        }

        if (!this.isActive) {
            this.focus();
            return true;
        }

        this.isSelected = true;
        this.cursorPosition = this.value.length;
        return true;
    }

    handleKeyEvent(event: KeyboardEvent): boolean {
        if (!this.isActive) return false;

        let handled = false;

        if (event.key === 'Tab') {
            return false; // Let the property editor handle tab navigation
        }

        if (this.isSelected) {
            switch (event.key) {
                case 'Backspace':
                case 'Delete':
                    this.displayValue = '0';
                    this.cursorPosition = 1;
                    this.isSelected = false;
                    handled = true;
                    break;
                default:
                    if (event.key.match(/^[0-9-]$/)) {
                        this.displayValue = event.key;
                        this.cursorPosition = 1;
                        this.isSelected = false;
                        handled = true;
                    }
                    break;
            }
        } else {
            switch (event.key) {
                case 'Backspace':
                    if (this.cursorPosition > 0) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition - 1) + this.displayValue.slice(this.cursorPosition);
                        this.cursorPosition--;
                        handled = true;
                    }
                    break;
                case 'Delete':
                    if (this.cursorPosition < this.displayValue.length) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition) + this.displayValue.slice(this.cursorPosition + 1);
                        handled = true;
                    }
                    break;
                case 'ArrowLeft':
                    if (this.cursorPosition > 0) {
                        this.cursorPosition--;
                        handled = true;
                    }
                    break;
                case 'ArrowRight':
                    if (this.cursorPosition < this.displayValue.length) {
                        this.cursorPosition++;
                        handled = true;
                    }
                    break;
                default:
                    if (event.key.match(/^[0-9-]$/)) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition) + event.key + this.displayValue.slice(this.cursorPosition);
                        this.cursorPosition++;
                        handled = true;
                    }
                    break;
            }
        }

        if (handled) {
            // Validate and normalize the angle value
            const angle = parseFloat(this.displayValue);
            if (!isNaN(angle)) {
                // Normalize angle to -180 to 180 degrees
                const normalizedAngle = ((angle % 360 + 540) % 360) - 180;
                this.value = Math.round(normalizedAngle);
                this.propertyEditor.commitValueFromInput(this, this.value);
            }
        }

        return handled;
    }

    focus(): void {
        this.isActive = true;
        this.isSelected = true;
        this.cursorPosition = this.value.length;
    }

    blur(): void {
        this.isActive = false;
        this.isSelected = false;
    }

    override setValue(value: any): void {

        this.value = parseInt(value);
        if(this.value < 0 || this.value > 360) {
            this.value = ((this.value % 360) + 360) % 360;
        }
        this.displayValue = this.value?.toString();
    }

    private degreesToRadians(degrees: number): number {
        return (degrees * Math.PI) / 180;
    }

    private radiansToDegrees(radians: number): number {
        return (radians * 180) / Math.PI;
    }
} 
